/**
 * Survival analysis that keeps the units which have not failed.
 *
 * The classic error in reliability estimation is fitting on failure times
 * alone and dropping every unit still running. That is right-censoring
 * discarded, and it does not cancel out: dropping the survivors removes
 * exactly the long-lived units, so both the shape and the characteristic life
 * come out too low and the fleet looks like it fails sooner than it does.
 *
 * UNDER-ESTIMATING LIFE IS NOT THE SAFE WAY TO BE WRONG. It replaces
 * serviceable fittings, floods the operator with alerts that do not come true,
 * and ends with somebody switching the predictions off — at which point the
 * platform predicts nothing at all. So `censored` is shown beside `failures`
 * on every fit: it is the number that says whether the estimate is sound.
 *
 * AND NOTHING IS EXTRAPOLATED SILENTLY. A Weibull will cheerfully report a p90
 * life of 40,000 h from data whose latest observed failure was at 9,000. That
 * number is a property of the model, not of the fleet, so a quantile beyond
 * the observation horizon is labelled wherever it is shown.
 */

export interface Population {
  population: string;
  timeUnit: string;
  units: number;
  failures: number;
  failureModes?: number;
  /** False when there is too little to fit — not a fit of zero. */
  fittable: boolean;
  whyNot?: string;
}

export interface Fit {
  id: string;
  population: string;
  method: string;
  units: number;
  failures: number;
  /** The units still running. Dropping these is the classic error. */
  censored: number;
  beta: number;
  betaLow?: number;
  betaHigh?: number;
  eta: number;
  timeUnit: string;
  /** Beyond this, every quantile is a property of the model, not the fleet. */
  lastFailureAt?: number;
  fittedAt?: string;
  shapeVerdict?: string;
  wearOut?: {
    known: boolean;
    verdict?: string;
    note?: string;
    rejectsConstantRate?: boolean;
    likelihoodRatio?: number;
    critical95?: number;
  };
}

/** What the shape parameter says about how this population fails. */
export type Shape = "infant-mortality" | "constant" | "wear-out" | "uncertain";

/**
 * Read from the CONFIDENCE INTERVAL, not the point estimate.
 *
 * A beta of 1.2 whose interval spans 0.8 to 1.9 says nothing about wear-out,
 * and calling it wear-out would licence an age-based replacement programme on
 * a population that may have a constant failure rate. Only an interval wholly
 * above or below 1.0 is a finding.
 */
export function shape(f: Fit): Shape {
  if (f.betaLow == null || f.betaHigh == null) return f.beta > 1 ? "uncertain" : "uncertain";
  if (f.betaLow > 1) return "wear-out";
  if (f.betaHigh < 1) return "infant-mortality";
  return "constant";
}

export const SHAPE_LABEL: Record<Shape, string> = {
  "wear-out": "Wears out with age",
  "infant-mortality": "Fails early or not at all",
  constant: "Constant failure rate",
  uncertain: "Shape not established",
};

/**
 * What proportion of the sample would be thrown away by a failures-only fit.
 *
 * This is the headline number on the page: it is the size of the error the
 * module exists to avoid, expressed as the share of the fleet at stake.
 */
export function censoredPct(f: Fit): number | null {
  if (!f.units) return null;
  return Math.round((f.censored / f.units) * 100);
}

/**
 * Whether a life estimate lies beyond anything actually observed.
 *
 * True means the figure is a property of the model. It is still worth showing
 * — it is the best estimate available — but never without the label.
 */
export function beyondObservation(f: Fit, atTime: number): boolean {
  return f.lastFailureAt != null && atTime > f.lastFailureAt;
}
